import { useMemo } from 'react';
import * as Yup from 'yup';
import { useTranslation } from 'react-i18next';

import { TextField, Radio, CardCheckbox, ElinxNotificationMethod, FieldData, Tooltip, NumbersOnly } from 'vopay';

import { PaymentFieldDefault } from './types';

export function useElinxSettingsFields(
  defaultNotificationMethod: ElinxNotificationMethod,
  country: string,
): FieldData<PaymentFieldDefault>[] {
  const { t } = useTranslation('translations', { keyPrefix: 'UseTransactionCreationFields' });
  return useMemo(() => {
    return [
      {
        label: () => t('Fields.sendNotification.label'),
        fieldName: 'SendNotification',
        getInputState: () => 'optional',
        showField: (values) => values.ProvideOrRequestPaymentInformation === 'request',
        serializeField: (values) => values.SendNotification,
        renderField: ({ getFormikData }) => {
          const fieldData = getFormikData<boolean>({});
          return (
            <CardCheckbox
              indicator="switch"
              checked={fieldData.value}
              label={t('Fields.sendNotification.placeholder')}
              {...fieldData}
            />
          );
        },
      },
      {
        label: () => t('Fields.sendReminder.label'),
        fieldName: 'SendReminder',
        getInputState: () => 'optional',
        showField: (values) => values.ProvideOrRequestPaymentInformation === 'request',
        serializeField: (values) => values.SendReminder,
        renderField: ({ getFormikData }) => {
          const fieldData = getFormikData<boolean>({});
          return (
            <CardCheckbox
              indicator="switch"
              checked={fieldData.value}
              label={t('Fields.sendReminder.checkboxLabel')}
              {...fieldData}
            />
          );
        },
      },
      {
        label: () => t('Fields.reminderFrequency.label'),
        fieldName: 'ReminderFrequency',
        getInputState: (ctx) => (ctx.values.SendReminder ? 'required' : 'disabled'),
        showField: (values) => values.ProvideOrRequestPaymentInformation === 'request',
        serializeField: (values) => values.ReminderFrequency,
        renderField: ({ getFormikData }) => {
          const fieldData = getFormikData<string>({});
          return <TextField variant="outlineShadow" placeholder="" {...fieldData} />;
        },
        validate: (ctx) => (ctx.values.SendReminder ? Yup.mixed().required() : Yup.mixed()),
      },
      {
        label: () => t('Fields.notificationMethod.label'),
        fieldName: 'NotificationMethod',
        getInputState: (ctx) => (ctx.values.SendNotification ? 'optional' : 'disabled'),
        showField: (values) => values.ProvideOrRequestPaymentInformation === 'request',
        serializeField: (values) => values.NotificationMethod,
        renderField: ({ getFormikData }) => {
          const fieldData = getFormikData<string>({});
          const options = {
            email: t('notificationOptions.email'),
            sms: t('notificationOptions.sms'),
            ...(defaultNotificationMethod === 'whatsapp' && {
              whatsapp: t('notificationOptions.whatsapp'),
            }),
          };
          return <Radio variant="outlineShadow" options={options} {...fieldData} />;
        },
      },
      {
        label: () => t('Fields.recipientEmailAddress.label'),
        fieldName: 'RecipientEmailAddress',
        getInputState: (ctx) => (ctx.values.NotificationMethod === 'email' ? 'required' : 'disabled'),
        showField: (values) =>
          values.ProvideOrRequestPaymentInformation === 'request' &&
          !!values.SendNotification &&
          values.NotificationMethod === 'email',
        serializeField: (values) => values.RecipientEmailAddress,

        renderField: ({ getFormikData }) => {
          const fieldData = getFormikData<string>({});
          return <TextField variant="outlineShadow" placeholder="" preset="email" {...fieldData} />;
        },
        validate: (ctx) =>
          ctx.values.NotificationMethod === 'email' ? Yup.string().email().required() : Yup.string().email(),
      },
      {
        label: () => t('Fields.receiverPhoneNumber.label'),
        fieldName: 'ReceiverPhoneNumber',
        getInputState: (ctx) =>
          ctx.values.NotificationMethod === 'sms' || ctx.values.NotificationMethod === 'whatsapp'
            ? 'required'
            : 'disabled',
        showField: (values) =>
          values.ProvideOrRequestPaymentInformation === 'request' &&
          !!values.SendNotification &&
          (values.NotificationMethod === 'sms' || values.NotificationMethod === 'whatsapp') &&
          !(!!values.PaymentSelectionMethod && values.PaymentSelectionMethod.includes('bank') && country === 'US'),
        serializeField: (values) => NumbersOnly(values.ReceiverPhoneNumber || ''),
        tooltip: () => <Tooltip information={t('Fields.receiverPhoneNumber.tooltipInfo')} position="top" />,
        renderField: ({ getFormikData }) => {
          const fieldData = getFormikData<string>({});
          return <TextField variant="outlineShadow" placeholder="" {...fieldData} />;
        },
        validate: (ctx) =>
          ctx.values.NotificationMethod === 'sms' || ctx.values.NotificationMethod === 'whatsapp'
            ? Yup.string().required()
            : Yup.string(),
      },
    ];
  }, [defaultNotificationMethod, country]);
}
